import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Icon } from '@kognitos/lattice';

import type { IRainToolCallState } from '../hooks/types';
import { RainToolCallDetails } from './RainToolCallDetails';

export interface IRainRawToolCallProps {
  state: IRainToolCallState;
}

/**
 * The raw arguments and result behind a bespoke card, as an opt-in disclosure.
 *
 * Cards like the connection picker and `attach_file` keep their own UI under
 * full verbosity — they block on user input, so replacing one with a read-only
 * payload card would leave the agent waiting on something the user can no longer
 * reach. That left a hole: a surface claiming to show the entire thread showed
 * everything EXCEPT its interactive calls. This closes it without touching what
 * makes those cards answerable.
 *
 * Closed by default and quiet in the corner, because on those cards the thing
 * the user came for is the picker, not the payload.
 */
export function RainRawToolCall({ state }: IRainRawToolCallProps) {
  const { t } = useTranslation('rain');
  const [open, setOpen] = useState(false);

  return (
    <Flex direction="column" gap={2} width="full" className="min-w-0">
      <Button
        variant="ghost"
        size="sm"
        className="h-6 self-start gap-1 px-1 text-[11px] text-muted-foreground"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
        data-testid="rain-raw-tool-call-toggle"
      >
        <Icon type={open ? 'ChevronDown' : 'ChevronRight'} size="sm" />
        {open ? t('rain.toolCall.hideRaw') : t('rain.toolCall.viewRaw')}
      </Button>

      {open && (
        <div
          className="w-full min-w-0 rounded-lg bg-secondary/50 p-3"
          data-testid="rain-raw-tool-call"
        >
          <RainToolCallDetails
            status={state.status}
            args={state.args}
            result={state.result}
            errorMessage={state.errorMessage}
            awaitingApproval={state.awaitingApproval}
          />
        </div>
      )}
    </Flex>
  );
}
